import { MantineReactTable, type MRT_RowData } from "@ngt/mantine-react-table";
import { memo } from "react";

export type DataGridProps<TData extends MRT_RowData> = React.ComponentProps<
    typeof MantineReactTable<TData>
>;

export type DataGridBodyRowProps<TData extends MRT_RowData> =
    DataGridProps<TData>["mantineTableBodyRowProps"];

export const DefaultDataGridProps = {
    layoutMode: "grid",
    initialState: {
        density: "xs",
    },
    mantineTableProps: {
        component: "div",
        striped: true,
    },
    mantineTableHeadProps: {
        component: "div",
    },
    mantineTableHeadRowProps: {
        component: "div",
    },
    mantineTableHeadCellProps: {
        component: "div",
    },
    mantineTableBodyProps: {
        component: "div",
    },
    mantineTableBodyRowProps: {
        component: "div",
    },
    mantineTableBodyCellProps: {
        component: "div",
    },
    mantineTableFooterProps: {
        component: "div",
    },
    mantineTableFooterRowProps: {
        component: "div",
    },
    mantineTableFooterCellProps: {
        component: "div",
        style: {
            padding: "var(--table-vertical-spacing) var(--table-horizontal-spacing)",
        },
    },
    enableSorting: true,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableColumnOrdering: false,
    enableColumnPinning: false,
    enableColumnResizing: false,
} as DataGridProps<MRT_RowData>;

export const createInactiveDataGridBodyRowProps = <TData extends MRT_RowData>(
    activeProperty: keyof TData,
): NonNullable<DataGridBodyRowProps<TData>> =>
    (({ row }) => ({
        style: {
            color: row.original[activeProperty] === true ? "inherit" : "var(--mantine-color-red-9)",
        },
    })) as NonNullable<DataGridBodyRowProps<TData>>;

export const mergeDataGridBodyRowProps = <TData extends MRT_RowData>(
    callbackProps: unknown,
    configuredProps?: DataGridBodyRowProps<TData>,
    overrideProps?: Record<string, unknown>,
) => {
    const configured =
        typeof configuredProps === "function"
            ? configuredProps(callbackProps as never)
            : (configuredProps ?? {});
    const overrides = overrideProps ?? {};

    return {
        ...DefaultDataGridProps.mantineTableBodyRowProps,
        ...configured,
        ...overrides,
        style: {
            ...((configured as { style?: object }).style ?? {}),
            ...((overrides as { style?: object }).style ?? {}),
        },
    };
};

const DataGrid = <TData extends MRT_RowData>(props: DataGridProps<TData>) => {
    const initialState = {
        ...DefaultDataGridProps.initialState,
        ...props.initialState,
    };

    return (
        <MantineReactTable
            {...(DefaultDataGridProps as any)}
            {...props}
            initialState={initialState}
        />
    );
};

export default memo(DataGrid) as typeof DataGrid;
